// client/src/pages/ClientAIOptimizations.js

import GovernedProviderEvidence from "../components/GovernedProviderEvidence";
import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import "./ClientAIOptimizations.css";
import "./CampaignInsightsDesktopWidth.css";
import "./CampaignInsightsStatusGroup.css";
import { useNavigate } from "react-router-dom";
import PoweredByLogo from "../components/PoweredByLogo";
import InsightNarrativeRenderer from "../components/InsightNarrativeRenderer";
import IntelligenceProvenanceBadge from "../components/IntelligenceProvenanceBadge";
import QualifiedVideoSummary from "../components/QualifiedVideoSummary";
import { buildCurrentKpiLabel } from "../utils/intelligenceProvenance";
import {
  AdvertiserIdentityTrigger,
  GovernedAdvertiserSelectorModal,
} from "../components/GovernedAdvertiserSelector";
import useGovernedAdvertiserCatalog from "../hooks/useGovernedAdvertiserCatalog";
import { noStoreFetch } from "../utils/noStoreRequest";
import {
  DEFAULT_AI_OPTIMIZATION_KPIS as DEFAULT_KPIS,
  buildAiOptimizationKpiFields,
  extractAiOptimizationCurrentKpisFromPayload,
  extractAiOptimizationSavedSnapshotKpisFromPayload,
  hasAnyAiOptimizationKpiValues,
  resolveAiOptimizationKpisFromPayloads,
} from "../utils/aiOptimizationKpiParity";
import { resolveAiOptimizationKpiStatus } from "../utils/aiOptimizationKpiStatus";
import { normalizeAdvertiserVertical } from "../config/verticalDashboardConfig";
import {
  getVerticalIntelligenceConfig,
  sanitizeVerticalIntelligenceCopy,
  serializeVerticalIntelligence,
} from "../config/verticalIntelligenceConfig";

/**
 * ✅ CRA-safe only
 * - Removed import.meta entirely to avoid CRA parse/runtime issues
 */
const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:5000";
const CPHERE_INSIGHTS_HERO_URL = `${String(API_BASE_URL || "http://localhost:5000").replace(/\/$/, "")}/email-assets/cphereInsights.jpg`;
const KPI_REFRESH_INTERVAL_MS = 60000;

const debugInsightsDiagnostics = (message, payload) => {
  if (process.env.NODE_ENV !== "production") {
    console.debug(message, payload);
  }
};

const isAbortLikeError = (error) =>
  error?.name === "AbortError" || error?.code === 20 || /abort/i.test(String(error?.message || ""));

const CLIENT_FORBIDDEN_OPTIMIZATION_TEXT_RE =
  /\b(?:internal|reconciliation|reconcile|vendor\s+cost|provider\s+cost|cost\s+basis|raw\s+(?:spend|cpm|cost|media)|ingestion\s+cpm|platform\s+cost|margin|markup|profit|data[_\s-]?source|live_meta|providerstates?|source\s+diagnostics?|billing\s+diagnostics?|contributor\s+audit)\b/i;

const containsForbiddenClientOptimizationText = (value) => {
  if (value == null) return false;
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
    return CLIENT_FORBIDDEN_OPTIMIZATION_TEXT_RE.test(String(value));
  }
  if (Array.isArray(value)) {
    return value.some((item) => containsForbiddenClientOptimizationText(item));
  }
  if (typeof value === "object") {
    return Object.values(value).some((item) => containsForbiddenClientOptimizationText(item));
  }
  return false;
};

const isClientSafeOptimizationNarrative = (...values) =>
  !values.some((value) => containsForbiddenClientOptimizationText(value));

const sanitizeAiOptimizationNarrativeValue = (value, advertiserVertical) => {
  if (typeof value === "string") {
    return sanitizeVerticalIntelligenceCopy(value, advertiserVertical);
  }
  if (Array.isArray(value)) {
    return value.map((item) => sanitizeAiOptimizationNarrativeValue(item, advertiserVertical));
  }
  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value).map(([key, item]) => [
        key,
        sanitizeAiOptimizationNarrativeValue(item, advertiserVertical),
      ])
    );
  }
  return value;
};

const AiOptimizationPanelHero = ({
  title,
  subtitle = "",
  eyebrow = "Cphere Insights",
}) => (
  <div className="aiopt-panel-hero" aria-hidden="true">
    <div className="aiopt-panel-hero-art">
      <img
        className="aiopt-panel-hero-img"
        src={CPHERE_INSIGHTS_HERO_URL}
        alt=""
        loading="lazy"
        draggable={false}
      />
    </div>
    <div className="aiopt-panel-hero-copy">
      <div className="aiopt-panel-hero-kicker">{eyebrow}</div>
      <div className="aiopt-panel-hero-title">{title}</div>
      {subtitle ? <div className="aiopt-panel-hero-subtitle">{subtitle}</div> : null}
    </div>
  </div>
);

const KPI_SIGNATURE_FIELDS = [
  "adSpend",
  "impressions",
  "clicks",
  "ctr",
  "cpc",
  "engagements",
  "engagementRate",
  "videoViews",
  "videoViewRate",
  "videoCompletions",
  "videoCompletionRate",
  "conversions",
];

const normalizeKpiSignatureValue = (value) => {
  const text = String(value ?? "").trim();
  if (!text || /^no\s+data$/i.test(text)) return null;
  const numeric = Number(text.replace(/[$,%\s]/g, ""));
  if (Number.isFinite(numeric)) return Number(numeric.toFixed(2));
  return text.toUpperCase();
};

const savedKpisMatchCurrent = (savedKpis, currentKpis) => {
  if (!hasAnyAiOptimizationKpiValues(currentKpis)) return false;
  if (!hasAnyAiOptimizationKpiValues(savedKpis)) return false;

  return KPI_SIGNATURE_FIELDS.every((field) => {
    const currentValue = normalizeKpiSignatureValue(currentKpis?.[field]);
    if (currentValue === null) return true;
    return normalizeKpiSignatureValue(savedKpis?.[field]) === currentValue;
  });
};

const extractSavedSnapshotKpis = (payload) =>
  extractAiOptimizationSavedSnapshotKpisFromPayload(payload, DEFAULT_KPIS);

const extractCurrentCanonicalKpis = (payload) =>
  extractAiOptimizationCurrentKpisFromPayload(payload, DEFAULT_KPIS);

const buildNarrativePanelMessage = ({
  field,
  visibilityState,
  diagnosticText,
  currentKpisAvailable,
  savedNarrativeMatchesCurrent,
  savedNarrativeClientSafe,
} = {}) => {
  const label = field === "strategy" ? "Campaign Strategy" : "Campaign Notes";
  const normalizedVisibility = String(visibilityState || "").trim().toLowerCase();
  const cleanedDiagnostic = String(diagnosticText || "").trim();

  if (normalizedVisibility === "draft_only_unpublished") {
    return "";
  }

  if (normalizedVisibility === "empty") {
    return "";
  }

  if (!savedNarrativeClientSafe) {
    return `${label} is unavailable until it is regenerated for the current metric set.`;
  }

  if (currentKpisAvailable && savedNarrativeMatchesCurrent === false) {
    return `${label} is saved from a prior KPI snapshot. Run Analysis to refresh it.`;
  }

  return cleanedDiagnostic;
};

const hasInsightValue = (value) => {
  if (typeof value === "string") return Boolean(value.trim());
  if (Array.isArray(value)) return value.length > 0;
  if (value && typeof value === "object") return Object.keys(value).length > 0;
  return Boolean(value);
};

const hasSavedAiOptimizationIntelligence = (...values) =>
  values.some((value) => hasInsightValue(value));

const clearKpiFreshnessPanelMessage = (message) =>
  /prior KPI snapshot|current metric set/i.test(String(message || "")) ? "" : message;

const getNarrativeEmptyText = (field, message) => {
  const fallback = field === "strategy" ? "No strategy yet…" : "No notes yet…";
  return String(message || "").trim() || fallback;
};

const ClientAIOptimizations = () => {
  const navigate = useNavigate();

  const [currentKpiLoadError, setCurrentKpiLoadError] = useState("");
  const [platformCardLoadError, setPlatformCardLoadError] = useState("");

  const [optimizationNotes, setOptimizationNotes] = useState("");
  const [optimizationNotesStructured, setOptimizationNotesStructured] = useState(null);
  const [strategyCopy, setStrategyCopy] = useState("");
  const [strategyCopyStructured, setStrategyCopyStructured] = useState(null);
  const [kpiData, setKpiData] = useState(DEFAULT_KPIS);
  /* The qualified-video basis travels beside the KPI strip: the headline
     count is one of the cards, and this carries the contributions that
     make it readable. Cleared wherever the KPIs are, so a stale video
     story can never outlive the numbers it explains. */
  const [qualifiedVideo, setQualifiedVideo] = useState(null);
  const [providerEvidencePayload, setProviderEvidencePayload] = useState(null);
  const [notesPanelMessage, setNotesPanelMessage] = useState("");
  const [strategyPanelMessage, setStrategyPanelMessage] = useState("");
  // The snapshot binding served alongside the narrative. Presentation only —
  // it never participates in deciding what text is rendered.
  const [intelligenceProvenance, setIntelligenceProvenance] = useState(null);

  const [, setLoading] = useState(false);

  const [statusMessage, setStatusMessage] = useState("");
  const [statusType, setStatusType] = useState("info");
  const [lastSavedAt, setLastSavedAt] = useState(null);

  const lastLoadedIdRef = useRef("");
  const mountedRef = useRef(false);
  const inFlightAbortRef = useRef(null);
  const kpiRefreshAbortRef = useRef(null);
  const currentKpiRequestSeqRef = useRef(0);
  const displayedNarrativeKpisRef = useRef(null);
  const baseOptimizationPayloadRef = useRef(null);
  const reportingAuthorityLossRef = useRef(null);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      currentKpiRequestSeqRef.current += 1;
      if (inFlightAbortRef.current) inFlightAbortRef.current.abort();
      if (kpiRefreshAbortRef.current) kpiRefreshAbortRef.current.abort();
      debugInsightsDiagnostics("[ClientAIOptimizations] cleanup on unmount", {
        abortedRequests: ["saved_optimization", "current_kpis"],
      });
    };
  }, []);

  const isActiveSignal = useCallback(
    (signal) => mountedRef.current && !signal?.aborted,
    []
  );

  const getAuthHeader = useCallback(() => {
    const token =
      localStorage.getItem("clientAuthToken") ||
      localStorage.getItem("authToken") ||
      localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, []);

  const setStatus = (msg = "", type = "info") => {
    setStatusMessage(msg);
    setStatusType(type);
  };

  /**
   * ✅ Governed advertiser authority
   * Campaign Insights renders the same canonical image-based selector as
   * Campaign Optimizer. Identity, visibility and authorization all come from
   * the shared governed catalog — this page keeps no advertiser list of its
   * own and issues no advertiser catalog or access request of its own.
   */
  const clearAdvertiserScopedPresentationState = useCallback(() => {
    lastLoadedIdRef.current = "";
    reportingAuthorityLossRef.current = null;
    currentKpiRequestSeqRef.current += 1;
    if (inFlightAbortRef.current) inFlightAbortRef.current.abort();
    if (kpiRefreshAbortRef.current) kpiRefreshAbortRef.current.abort();
    displayedNarrativeKpisRef.current = null;
    baseOptimizationPayloadRef.current = null;
    setOptimizationNotes("");
    setOptimizationNotesStructured(null);
    setStrategyCopy("");
    setStrategyCopyStructured(null);
    setNotesPanelMessage("");
    setStrategyPanelMessage("");
    setKpiData(DEFAULT_KPIS);
    setQualifiedVideo(null);
    setProviderEvidencePayload(null);
    setIntelligenceProvenance(null);
    setLastSavedAt(null);
    setCurrentKpiLoadError("");
    setPlatformCardLoadError("");
    setLoading(false);
    setStatusMessage("");
    setStatusType("info");
  }, []);

  const {
    selectedAdvertiserId,
    selectedAdvertiserName,
    visibleAdvertisers,
    currentAdvertiser,
    catalogReady,
    accessReady,
    loading: advertiserCatalogLoading,
    selectorDisabled,
    selectorEmpty,
    modalOpen: advertiserSelectorOpen,
    openSelector: openAdvertiserSelector,
    closeSelector: closeAdvertiserSelector,
    handleAdvertiserSelect,
  } = useGovernedAdvertiserCatalog({
    apiBase: API_BASE_URL,
    role: "client",
    onAdvertiserChange: clearAdvertiserScopedPresentationState,
  });

  // The confirmed governed record is the only advertiser scope this page will
  // load against. An id the governed catalog has not confirmed resolves to "",
  // which fails closed to the "choose an advertiser" state.
  const resolvedSelectedId = currentAdvertiser?.id
    ? String(currentAdvertiser.id)
    : "";
  const resolvedSelectedName =
    currentAdvertiser?.name || selectedAdvertiserName || "";

  const resolvedAdvertiserVertical = useMemo(
    () => normalizeAdvertiserVertical(currentAdvertiser?.vertical),
    [currentAdvertiser]
  );
  const advertiserVerticalContext = useMemo(
    () => serializeVerticalIntelligence(resolvedAdvertiserVertical),
    [resolvedAdvertiserVertical]
  );
  const advertiserVerticalGuidance = useMemo(
    () => getVerticalIntelligenceConfig(resolvedAdvertiserVertical),
    [resolvedAdvertiserVertical]
  );

  useEffect(() => {
    debugInsightsDiagnostics("[ClientAIOptimizations] split load errors", {
      currentKpiLoadError,
      platformCardLoadError,
    });
  }, [currentKpiLoadError, platformCardLoadError]);


  // A refusal or a successful withdrawal invalidates current numbers and their
  // saved fallback authority. Historical prose has no separate access contract
  // here, so it cannot remain after reporting authority is withdrawn.
  const applyReportingAuthorityLoss = useCallback((snapshot) => {
    if (!snapshot?.authorityDenied && !snapshot?.authorityWithdrawn) return false;
    reportingAuthorityLossRef.current = snapshot;
    displayedNarrativeKpisRef.current = null;
    baseOptimizationPayloadRef.current = null;
    setKpiData(Object.fromEntries(Object.keys(DEFAULT_KPIS).map(key => [key, "No Data"])));
    setQualifiedVideo(null);
    setIntelligenceProvenance(null);
    setProviderEvidencePayload(snapshot.authorityDenied ? null : snapshot.payload || null);
    setOptimizationNotes("");
    setOptimizationNotesStructured(null);
    setStrategyCopy("");
    setStrategyCopyStructured(null);
    setNotesPanelMessage("");
    setStrategyPanelMessage("");
    setLastSavedAt(null);
    setPlatformCardLoadError("");
    setStatusMessage(snapshot.authorityDenied
      ? `Access to current reporting was denied (HTTP ${snapshot.httpStatus}). Prior reporting has been cleared.`
      : "Current reporting is unavailable for this scope. Prior reporting has been cleared.");
    setStatusType(snapshot.authorityDenied ? "error" : "info");
    return true;
  }, []);

  const fetchCurrentKpisForAdvertiser = useCallback(async (safeId, signal) => {
    try {
      const currentUrl = `${API_BASE_URL}/api/advertiser-optimizations/${encodeURIComponent(
        safeId
      )}?currentKpisOnly=1`;
      const currentRes = await noStoreFetch(currentUrl, {
        headers: {
          "Cache-Control": "no-cache, no-store, max-age=0, must-revalidate",
          Pragma: "no-cache",
          ...getAuthHeader(),
        },
        signal,
      });
      if (!isActiveSignal(signal)) return { kpis: null, source: null };
      const currentJson = await currentRes.json().catch(() => null);
      if (!isActiveSignal(signal)) return { kpis: null, source: null };
      if (process.env.NODE_ENV !== "production") {
        console.log("[KPI FETCH]", {
          surface: "client_ai_optimizations",
          endpoint: currentUrl,
          status: currentRes.status,
          selectedAdvertiserId: safeId,
          selectedAdvertiserName: resolvedSelectedName,
          kpiSourceType: currentJson?.kpiSourceType || null,
          canonicalContract:
            currentJson?.canonicalMetrics?.summary?.contract ||
            currentJson?.canonicalReporting?.contract ||
            currentJson?.canonicalSummary?.contract ||
            null,
          canonicalSpend:
            currentJson?.canonicalMetrics?.summary?.aggregateTotals?.spend ??
            currentJson?.canonicalReporting?.aggregateTotals?.spend ??
            currentJson?.canonicalSummary?.aggregateTotals?.spend ??
            null,
          currentKpis: currentJson?.currentKpis || null,
          error: currentJson?.error || currentJson?.hint || currentJson?.debug?.error || null,
          response: currentJson,
        });
      }
      debugInsightsDiagnostics("[ClientAIOptimizations] currentKpisOnly fetch", {
        endpoint: currentUrl,
        status: currentRes.status,
        selectedAdvertiserId: safeId,
        selectedAdvertiserName: resolvedSelectedName,
      });
      if (currentRes.status === 401 || currentRes.status === 403) {
        const error = `Current reporting access denied (HTTP ${currentRes.status}).`;
        setCurrentKpiLoadError(error);
        return { kpis: null, source: null, payload: null, error, authorityDenied: true, httpStatus: currentRes.status };
      }
      if (currentRes.ok) {
        const summary = currentJson?.canonicalMetrics?.summary || currentJson?.canonicalReporting || currentJson?.canonicalSummary;
        if (summary?.contract === "canonical_reporting_v1" && summary.hasPerformanceData === false) {
          setCurrentKpiLoadError("");
          return { kpis: null, source: null, payload: currentJson, authorityWithdrawn: true };
        }
        const platformError = currentJson?.platformPerformanceDebug?.error || "";
        setPlatformCardLoadError(platformError ? String(platformError) : "");
        const currentKpis = extractCurrentCanonicalKpis(currentJson);
        if (currentKpis) {
          setCurrentKpiLoadError("");
          return {
            kpis: currentKpis,
            source: "advertiser_optimizations_canonical_current",
            payload: currentJson,
            authoritativeCurrent: currentJson?.kpiSourceType === "canonical_current" || summary?.contract === "canonical_reporting_v1",
          };
        }
        setCurrentKpiLoadError("Canonical current KPI payload did not contain visible KPI values.");
        return {
          kpis: null,
          source: null,
          error: "canonical_current_missing_values",
          payload: currentJson,
        };
      }
      const responseError =
        currentJson?.error ||
        currentJson?.hint ||
        `currentKpisOnly returned HTTP ${currentRes.status}`;
      setCurrentKpiLoadError(responseError);
      return { kpis: null, source: null, error: responseError, payload: currentJson };
    } catch (err) {
      if (isAbortLikeError(err) || signal?.aborted) throw err;
      if (!isActiveSignal(signal)) return { kpis: null, source: null };
      setCurrentKpiLoadError(err?.message || "Current KPI refresh failed.");
      debugInsightsDiagnostics("[ClientAIOptimizations] currentKpisOnly fetch", {
        selectedAdvertiserId: safeId,
        selectedAdvertiserName: resolvedSelectedName,
        error: err?.message || String(err),
      });
      return { kpis: null, source: null, error: err?.message || "Current KPI refresh failed." };
    }
  }, [getAuthHeader, isActiveSignal, resolvedSelectedName]);

  const loadData = useCallback(
    async (id) => {
      // The governed catalog only ever hands this page a confirmed, authorized
      // advertiser id, so authorization is not re-derived from a local list.
      const safeId = String(id || "").trim();
      if (!safeId) return;

      if (inFlightAbortRef.current) inFlightAbortRef.current.abort();
      const ac = new AbortController();
      inFlightAbortRef.current = ac;

      try {
        setLoading(true);
        const loadingKpiStatus = resolveAiOptimizationKpiStatus({
          isLoading: true,
        });
        setStatus(loadingKpiStatus.message, loadingKpiStatus.type);
        setOptimizationNotes("");
        setOptimizationNotesStructured(null);
        setStrategyCopy("");
        setStrategyCopyStructured(null);
        setNotesPanelMessage("");
        setStrategyPanelMessage("");
        displayedNarrativeKpisRef.current = null;
        baseOptimizationPayloadRef.current = null;
        setKpiData(DEFAULT_KPIS);
        setQualifiedVideo(null);
        setProviderEvidencePayload(null);
        setLastSavedAt(null);

        let nextNotes = "";
        let nextNotesStructured = null;
        let nextStrategy = "";
        let nextStrategyStructured = null;
        let nextKpis = { ...DEFAULT_KPIS };
        let savedKpisForSignature = { ...DEFAULT_KPIS };
        let nextLastSaved = null;
        let hasSavedOptimization = false;
        let nextNotesVisibilityState = "empty";
        let nextStrategyVisibilityState = "empty";
        let nextNotesDiagnosticText = "";
        let nextStrategyDiagnosticText = "";
        let baseOptimizationPayload = null;

        const url = `${API_BASE_URL}/api/advertiser-optimizations/${encodeURIComponent(
          safeId
        )}`;

        const res = await noStoreFetch(url, {
          headers: { ...getAuthHeader() },
          signal: ac.signal,
        });

        if (!isActiveSignal(ac.signal)) return;

        if (res.ok) {
          const json = await res.json();
          if (!isActiveSignal(ac.signal)) return;

          baseOptimizationPayload = json;
          baseOptimizationPayloadRef.current = json;
          hasSavedOptimization = true;
          nextNotes = sanitizeAiOptimizationNarrativeValue(
            json?.notes ?? "",
            resolvedAdvertiserVertical
          );
          nextNotesStructured = sanitizeAiOptimizationNarrativeValue(
            json?.notesStructured ?? null,
            resolvedAdvertiserVertical
          );
          nextStrategy = sanitizeAiOptimizationNarrativeValue(
            json?.strategyCopy ?? "",
            resolvedAdvertiserVertical
          );
          nextStrategyStructured = sanitizeAiOptimizationNarrativeValue(
            json?.strategyStructured ?? null,
            resolvedAdvertiserVertical
          );
          savedKpisForSignature =
            extractSavedSnapshotKpis(json) || { ...DEFAULT_KPIS };
          nextNotesVisibilityState = json?.notesVisibilityState || (nextNotes ? "published" : "empty");
          nextStrategyVisibilityState =
            json?.strategyVisibilityState || (nextStrategy ? "published" : "empty");
          nextNotesDiagnosticText = json?.notesDiagnosticText || "";
          nextStrategyDiagnosticText = json?.strategyDiagnosticText || "";

          const ts = json.updatedAt || json.lastSavedAt || json.createdAt || null;
          nextLastSaved = ts ? new Date(ts) : null;
        } else if (res.status === 401 || res.status === 403) {
          applyReportingAuthorityLoss({ authorityDenied: true, httpStatus: res.status });
          return;
        }

        const currentKpiRequestSeq = currentKpiRequestSeqRef.current + 1;
        currentKpiRequestSeqRef.current = currentKpiRequestSeq;

        const currentKpiSnapshot = await fetchCurrentKpisForAdvertiser(
          safeId,
          ac.signal
        );

        if (
          !isActiveSignal(ac.signal) ||
          currentKpiRequestSeq !== currentKpiRequestSeqRef.current
        ) {
          return;
        }

        if (applyReportingAuthorityLoss(currentKpiSnapshot)) return;
        if (currentKpiSnapshot?.authoritativeCurrent) reportingAuthorityLossRef.current = null;
        else if (applyReportingAuthorityLoss(reportingAuthorityLossRef.current)) return;

        setProviderEvidencePayload(currentKpiSnapshot?.payload || null);
        const resolvedKpiSnapshot = resolveAiOptimizationKpisFromPayloads({
          currentPayload: currentKpiSnapshot?.payload || null,
          basePayload: currentKpiSnapshot?.payload ? null : baseOptimizationPayload,
          base: DEFAULT_KPIS,
        });
        const displayKpis = hasAnyAiOptimizationKpiValues(resolvedKpiSnapshot?.kpis)
          ? resolvedKpiSnapshot.kpis
          : null;
        const effectiveCurrentKpis =
          resolvedKpiSnapshot?.hasCurrentKpis && displayKpis ? displayKpis : null;
        const savedNarrativeMatchesCurrent = savedKpisMatchCurrent(
          savedKpisForSignature,
          effectiveCurrentKpis
        );
        const savedNarrativeClientSafe = isClientSafeOptimizationNarrative(
          nextNotes,
          nextNotesStructured,
          nextStrategy,
          nextStrategyStructured
        );

        nextKpis = displayKpis
          ? { ...DEFAULT_KPIS, ...displayKpis }
          : { ...DEFAULT_KPIS };

        if (hasSavedOptimization && !savedNarrativeClientSafe) {
          nextNotes = "";
          nextNotesStructured = null;
          nextStrategy = "";
          nextStrategyStructured = null;
        }

        const nextNotesPanelMessage = buildNarrativePanelMessage({
          field: "notes",
          visibilityState: nextNotesVisibilityState,
          diagnosticText: nextNotesDiagnosticText,
          currentKpisAvailable: Boolean(effectiveCurrentKpis),
          savedNarrativeMatchesCurrent,
          savedNarrativeClientSafe,
        });
        const nextStrategyPanelMessage = buildNarrativePanelMessage({
          field: "strategy",
          visibilityState: nextStrategyVisibilityState,
          diagnosticText: nextStrategyDiagnosticText,
          currentKpisAvailable: Boolean(effectiveCurrentKpis),
          savedNarrativeMatchesCurrent,
          savedNarrativeClientSafe,
        });

        displayedNarrativeKpisRef.current =
          hasSavedOptimization && savedNarrativeMatchesCurrent && savedNarrativeClientSafe
            ? savedKpisForSignature
            : null;

        setOptimizationNotes(nextNotes);
        setOptimizationNotesStructured(nextNotesStructured);
        setStrategyCopy(nextStrategy);
        setStrategyCopyStructured(nextStrategyStructured);
        setNotesPanelMessage(nextNotesPanelMessage);
        setStrategyPanelMessage(nextStrategyPanelMessage);
        setKpiData(nextKpis);
        setLastSavedAt(nextLastSaved);
        // Served by the backend from the version's persisted snapshot binding.
        // Absent on rows written before binding existed, which renders as
        // "provenance unavailable" rather than as a guess.
        setIntelligenceProvenance(baseOptimizationPayload?.intelligenceProvenance || null);
        setQualifiedVideo(baseOptimizationPayload?.qualifiedVideo || null);

        const hasSavedIntelligence =
          hasSavedOptimization && savedNarrativeClientSafe
            ? hasSavedAiOptimizationIntelligence(
                nextNotes,
                nextNotesStructured,
                nextStrategy,
                nextStrategyStructured
              )
            : false;
        const resolvedKpiStatus = resolveAiOptimizationKpiStatus({
          hasCurrentKpis: Boolean(effectiveCurrentKpis),
          hasSavedIntelligence,
          savedIntelligenceFresh: savedNarrativeMatchesCurrent,
          currentKpiError:
            effectiveCurrentKpis
              ? ""
              : currentKpiSnapshot?.error || (!displayKpis ? "current_kpis_unavailable" : ""),
        });
        setStatus(resolvedKpiStatus.message, resolvedKpiStatus.type);
      } catch (e) {
        if (!isAbortLikeError(e) && isActiveSignal(ac.signal)) {
          console.error("Optimization fetch error:", e);
          setStatus(e?.message || "Failed to load optimizations.", "error");
        }
      } finally {
        if (isActiveSignal(ac.signal)) setLoading(false);
      }
    },
    [
      applyReportingAuthorityLoss,
      fetchCurrentKpisForAdvertiser,
      getAuthHeader,
      isActiveSignal,
      resolvedAdvertiserVertical,
    ]
  );

  const refreshCurrentKpis = useCallback(
    async (id) => {
      const safeId = String(id || "").trim();
      if (!safeId) return;

      if (kpiRefreshAbortRef.current) kpiRefreshAbortRef.current.abort();
      const ac = new AbortController();
      kpiRefreshAbortRef.current = ac;
      const currentKpiRequestSeq = currentKpiRequestSeqRef.current + 1;
      currentKpiRequestSeqRef.current = currentKpiRequestSeq;

      try {
        setProviderEvidencePayload(null);
        const currentKpiSnapshot = await fetchCurrentKpisForAdvertiser(safeId, ac.signal);
        if (
          !isActiveSignal(ac.signal) ||
          currentKpiRequestSeq !== currentKpiRequestSeqRef.current
        ) {
          return;
        }

        if (applyReportingAuthorityLoss(currentKpiSnapshot)) return;
        if (currentKpiSnapshot?.authoritativeCurrent) reportingAuthorityLossRef.current = null;
        else if (applyReportingAuthorityLoss(reportingAuthorityLossRef.current)) return;

        setProviderEvidencePayload(currentKpiSnapshot?.payload || null);
        if (currentKpiSnapshot?.error && !currentKpiSnapshot?.source) {
          setStatus("Current KPI refresh failed. Previously loaded metrics are shown; current reporting could not be verified.", "error");
          return;
        }
        const resolvedKpiSnapshot = resolveAiOptimizationKpisFromPayloads({
          currentPayload: currentKpiSnapshot?.payload || null,
          basePayload: currentKpiSnapshot?.payload ? null : baseOptimizationPayloadRef.current,
          base: DEFAULT_KPIS,
        });
        const displayKpis = hasAnyAiOptimizationKpiValues(resolvedKpiSnapshot?.kpis)
          ? resolvedKpiSnapshot.kpis
          : null;
        const effectiveCurrentKpis =
          resolvedKpiSnapshot?.hasCurrentKpis && displayKpis ? displayKpis : null;
        const hasSavedIntelligence = hasSavedAiOptimizationIntelligence(
          optimizationNotes,
          optimizationNotesStructured,
          strategyCopy,
          strategyCopyStructured
        );
        const currentKpisAvailable = Boolean(effectiveCurrentKpis);
        const existingFreshnessWarning =
          /prior KPI snapshot/i.test(notesPanelMessage) ||
          /prior KPI snapshot/i.test(strategyPanelMessage);
        let savedIntelligenceFresh = !existingFreshnessWarning;

        if (displayKpis) {
          setKpiData({ ...DEFAULT_KPIS, ...displayKpis });
          if (
            currentKpisAvailable &&
            displayedNarrativeKpisRef.current &&
            !savedKpisMatchCurrent(displayedNarrativeKpisRef.current, effectiveCurrentKpis)
          ) {
            displayedNarrativeKpisRef.current = null;
            savedIntelligenceFresh = false;
            setNotesPanelMessage(
              "Campaign Notes are saved from a prior KPI snapshot. Run Analysis to refresh them."
            );
            setStrategyPanelMessage(
              "Campaign Strategy is saved from a prior KPI snapshot. Run Analysis to refresh it."
            );
          }
          const resolvedKpiStatus = resolveAiOptimizationKpiStatus({
            hasCurrentKpis: currentKpisAvailable,
            hasSavedIntelligence,
            savedIntelligenceFresh,
            currentKpiError: currentKpisAvailable ? "" : currentKpiSnapshot?.error || "",
          });
          setStatus(resolvedKpiStatus.message, resolvedKpiStatus.type);
        } else {
          setNotesPanelMessage(clearKpiFreshnessPanelMessage);
          setStrategyPanelMessage(clearKpiFreshnessPanelMessage);
          const resolvedKpiStatus = resolveAiOptimizationKpiStatus({
            hasCurrentKpis: false,
            hasSavedIntelligence,
            currentKpiError: currentKpiSnapshot?.error || "current_kpis_unavailable",
          });
          setStatus(resolvedKpiStatus.message, resolvedKpiStatus.type);
        }
      } catch (err) {
        if (!isAbortLikeError(err) && isActiveSignal(ac.signal)) {
          console.error("Current KPI refresh error:", err);
        }
      } finally {
        if (kpiRefreshAbortRef.current === ac) {
          kpiRefreshAbortRef.current = null;
        }
      }
    },
    [
      applyReportingAuthorityLoss,
      fetchCurrentKpisForAdvertiser,
      isActiveSignal,
      notesPanelMessage,
      optimizationNotes,
      optimizationNotesStructured,
      strategyCopy,
      strategyCopyStructured,
      strategyPanelMessage,
    ]
  );

  useEffect(() => {
    if (!catalogReady || !accessReady) return;

    const id = (resolvedSelectedId || "").trim();
    if (!id) {
      setLoading(false);
      setStatus("Choose an advertiser to begin.", "info");
      displayedNarrativeKpisRef.current = null;
      return;
    }

    if (id === lastLoadedIdRef.current) return;
    lastLoadedIdRef.current = id;

    loadData(id);

    return () => {
      if (inFlightAbortRef.current) inFlightAbortRef.current.abort();
    };
  }, [catalogReady, accessReady, resolvedSelectedId, loadData]);

  useEffect(() => {
    const id = (resolvedSelectedId || "").trim();
    if (!id) return undefined;

    const refreshIfVisible = () => {
      if (document.visibilityState === "hidden") return;
      refreshCurrentKpis(id);
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") refreshCurrentKpis(id);
    };

    window.addEventListener("focus", refreshIfVisible);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    const intervalId = window.setInterval(refreshIfVisible, KPI_REFRESH_INTERVAL_MS);

    return () => {
      window.removeEventListener("focus", refreshIfVisible);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.clearInterval(intervalId);
      if (kpiRefreshAbortRef.current) kpiRefreshAbortRef.current.abort();
    };
  }, [resolvedSelectedId, refreshCurrentKpis]);

  const handleLogoClick = () => navigate("/dashboard");

  const statusClass =
    statusType === "error"
      ? "aiopt-status-pill aiopt-status-error"
      : statusType === "muted"
      ? "aiopt-status-pill aiopt-status-muted"
      : statusType === "success"
      ? "aiopt-status-pill aiopt-status-success"
      : "aiopt-status-pill aiopt-status-info";

  const metaFallback = resolvedSelectedId ? "Ready." : "Choose an advertiser to begin.";

  return (
    <div className="aiopt-page-container">
      <div className="aiopt-page-content">
        <PoweredByLogo
          alt="Powered by"
          className="aiopt-poweredby-logo"
          onClick={handleLogoClick}
        />

        <div className="cphere-governed-hero-selector-stack">
          <div className="aiopt-hero-container">
            <div className="aiopt-hero-pill">
              <span className="aiopt-hero-pill-label cphere-hero-pill-wordmark">Campaign</span>
              <span className="aiopt-hero-pill-tag cphere-hero-pill-tag">Insights</span>
            </div>
            <div className="aiopt-hero-shimmer-bar" />
          </div>

          <div className="cphere-governed-advertiser-selector-stage">
            <AdvertiserIdentityTrigger
              advertiser={currentAdvertiser}
              advertiserName={resolvedSelectedName}
              apiBase={API_BASE_URL}
              disabled={selectorDisabled}
              loading={advertiserCatalogLoading}
              empty={selectorEmpty}
              modalOpen={advertiserSelectorOpen}
              onClick={openAdvertiserSelector}
            />
          </div>
        </div>

        {advertiserSelectorOpen && visibleAdvertisers.length ? (
          <GovernedAdvertiserSelectorModal
            advertisers={visibleAdvertisers}
            selectedAdvertiserId={selectedAdvertiserId}
            selectedAdvertiserName={resolvedSelectedName}
            apiBase={API_BASE_URL}
            onSelect={handleAdvertiserSelect}
            onClose={closeAdvertiserSelector}
          />
        ) : null}

        <div className="aiopt-shell-card">
          <div className="aiopt-topbar">
            <div className="aiopt-meta">
              {statusMessage ? (
                <div className={statusClass}>{statusMessage}</div>
              ) : (
                <div className="aiopt-status-pill aiopt-status-muted">{metaFallback}</div>
              )}

              <div className="aiopt-last-saved">
                <span className="aiopt-last-saved-label">Last Saved</span>
                <span className="aiopt-last-saved-value">
                  {lastSavedAt ? lastSavedAt.toLocaleString() : "—"}
                </span>
              </div>
            </div>
          </div>

          <div className="aiopt-section-head aiopt-section-head-kpis">
            <div className="aiopt-section-pill aiopt-section-pill-kpis">Current KPI Snapshot</div>
            {buildCurrentKpiLabel(intelligenceProvenance) ? (
              <div className="aiopt-kpi-updated-label">
                {buildCurrentKpiLabel(intelligenceProvenance)}
              </div>
            ) : null}
          </div>

          <div className={`aiopt-kpis aiopt-kpis-top ${resolvedSelectedId ? "" : "aiopt-disabled"}`}>
            {buildAiOptimizationKpiFields(qualifiedVideo).map(([field, label]) => (
              <div className="aiopt-kpi-card" key={field}>
                <h2 className="aiopt-kpi-label">{label}</h2>
                <input
                  className="aiopt-kpi-input aiopt-kpi-input-center"
                  value={kpiData[field] ?? ""}
                  placeholder="—"
                  readOnly
                  disabled={!resolvedSelectedId}
                />
              </div>
            ))}
          </div>

          {/*
            The Qualified Video Views card above states the campaign total;
            this states which platforms produced it and at what native rate.
            Neither is publishable alone — a total with no attribution reads
            as one homogeneous measurement, which is precisely how the old
            video headline misled.
          */}
          <GovernedProviderEvidence payload={providerEvidencePayload} />
          <QualifiedVideoSummary
            qualifiedVideo={qualifiedVideo}
            disabled={!resolvedSelectedId}
          />

          {resolvedSelectedId && (
            <>
              <div className="aiopt-section-head">
                <div className="aiopt-section-pill">Insights</div>
              </div>

              <div className="aiopt-textgrid">
                <div className="aiopt-textpanel">
                  <div className="aiopt-panel-title">Campaign Notes</div>
                  <AiOptimizationPanelHero
                    title="Campaign Notes"
                    subtitle={`Narrative intelligence for the live KPI set, framed for ${advertiserVerticalGuidance.displayLabel} strategy.`}
                  />
                  <IntelligenceProvenanceBadge provenance={intelligenceProvenance} />
                  {notesPanelMessage ? (
                    <div className="aiopt-narrative-message">{notesPanelMessage}</div>
                  ) : null}
                  <div className="aiopt-rich-output" aria-label="Campaign Notes">
                    <InsightNarrativeRenderer
                      structured={optimizationNotesStructured}
                      value={optimizationNotes}
                      emptyText={getNarrativeEmptyText("notes", notesPanelMessage)}
                    />
                  </div>
                </div>

                <div className="aiopt-textpanel">
                  <div className="aiopt-panel-title">Campaign Strategy</div>
                  <AiOptimizationPanelHero
                    title="Campaign Strategy"
                    subtitle={`Strategic guidance framed by the current performance window and ${advertiserVerticalContext.label} planning context.`}
                  />
                  <IntelligenceProvenanceBadge provenance={intelligenceProvenance} />
                  {strategyPanelMessage ? (
                    <div className="aiopt-narrative-message">{strategyPanelMessage}</div>
                  ) : null}
                  <div className="aiopt-rich-output" aria-label="Campaign Strategy">
                    <InsightNarrativeRenderer
                      structured={strategyCopyStructured}
                      value={strategyCopy}
                      emptyText={getNarrativeEmptyText("strategy", strategyPanelMessage)}
                    />
                  </div>
                </div>
              </div>
            </>
          )}

          {!resolvedSelectedId && (
            <div className="aiopt-empty-hint">
              Select an advertiser to view KPI snapshot and campaign intelligence fields.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ClientAIOptimizations;
